/**
 * First-touch attribution for lead records.
 *
 * On the visitor's first pageview of the session we snapshot where they came
 * from (landing path, referrer, UTM params, gclid) into sessionStorage. When a
 * lead form is submitted (however many pages later) the snapshot is attached
 * to the payload, so every lead in the backend carries its source and the
 * founder can see which channel/page produces demos, not just traffic.
 *
 * sessionStorage (not localStorage) is deliberate: it scopes attribution to
 * the visit, dies with the tab, and stores no cross-session identifier,
 * keeping this plain first-party context rather than persistent tracking.
 * No-ops during react-snap pre-rendering.
 *
 * A visit that comes from an AI assistant (see aiReferral.ts) also fires one
 * `ai_assistant_visit` analytics event per session, with the assistant's name,
 * the landing path and its language, so AI-sent traffic can be counted on its
 * own. The first touch is never overwritten within a session, and a visit
 * without a referrer or campaign tag stays unattributed (direct traffic is not
 * evidence of AI discovery).
 */
import { aiAssistantSource } from './aiReferral';
import { trackEvent } from './analytics';

export interface Attribution {
  landingPath: string;
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  gclid: string;
}

const STORAGE_KEY = 'st_attribution';

const isPrerenderOrServer = (): boolean =>
  typeof window === 'undefined' ||
  typeof document === 'undefined' ||
  navigator.userAgent === 'ReactSnap';

/**
 * Record the first-touch snapshot if this session doesn't have one yet.
 * Call once on app mount, before any navigation rewrites the URL.
 */
export const captureAttribution = (): void => {
  if (isPrerenderOrServer()) return;
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {
      landingPath: window.location.pathname,
      referrer: document.referrer || '',
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmTerm: params.get('utm_term') || '',
      utmContent: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));

    const aiSource = aiAssistantSource(attribution.referrer, attribution.utmSource);
    if (aiSource) {
      const english =
        attribution.landingPath === '/en' || attribution.landingPath.startsWith('/en/');
      // Path only (no query string), so no campaign or personal parameters leak.
      trackEvent('ai_assistant_visit', {
        ai_source: aiSource,
        landing_page: attribution.landingPath,
        page_language: english ? 'en' : 'tr',
      });
    }
  } catch {
    // Storage can be unavailable (private mode quirks); attribution is
    // best-effort and must never break the page.
  }
};

/** The session's first-touch snapshot, or null when none was captured. */
export const getAttribution = (): Attribution | null => {
  if (isPrerenderOrServer()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};
